import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { marked } from "marked";
import { Plus, Pencil, Trash2, LogOut, Eye, EyeOff, Upload, FileText } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const emptyForm = {
  title: "",
  slug: "",
  date: new Date().toISOString().slice(0, 10),
  author: "Dr. Ben E. Magee, Founder",
  category: "Research",
  summary: "",
  body: "",
  pdf: "",
  coming_soon: false,
};

const inputCls =
  "w-full border border-[#D4D8E0] bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-[#B08D3F]";
const labelCls = "mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-[#5A6B8C]";

const TABS = [
  { id: "articles", label: "Articles" },
  { id: "submissions", label: "Submissions" },
  { id: "settings", label: "Settings" },
];

export default function Admin() {
  useSeo("Admin | Reform Intelligence®", "Reform Intelligence content management.");
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("articles");
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showPreview, setShowPreview] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [subs, setSubs] = useState([]);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });

  useEffect(() => {
    axios.get(`${API}/auth/me`)
      .then((r) => { setUser(r.data); loadArticles(); })
      .catch(() => navigate("/admin/login"))
      .finally(() => setChecking(false));
  }, [navigate]);

  const loadArticles = () =>
    axios.get(`${API}/admin/articles`).then((r) => setArticles(r.data)).catch(() => setArticles([]));

  const loadSubmissions = () => {
    axios.get(`${API}/admin/leads`).then((r) => setLeads(r.data)).catch(() => setLeads([]));
    axios.get(`${API}/admin/subscribers`).then((r) => setSubs(r.data)).catch(() => setSubs([]));
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const startNew = () => { setForm(emptyForm); setEditing("new"); setNotice(""); setError(""); setShowPreview(false); };
  const startEdit = (a) => {
    setForm({ title: a.title, slug: a.slug, date: a.date || "", author: a.author, category: a.category, summary: a.summary, body: a.body || "", pdf: a.pdf || "", coming_soon: !!a.coming_soon });
    setEditing(a.slug); setNotice(""); setError(""); setShowPreview(false);
  };

  const uploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd);
      setForm((f) => ({ ...f, pdf: data.path }));
      setNotice(`PDF uploaded: ${data.filename}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setError(""); setNotice("");
    const payload = { ...form, pdf: form.pdf || null };
    try {
      if (editing === "new") {
        await axios.post(`${API}/admin/articles`, payload);
        setNotice("Article published.");
      } else {
        await axios.put(`${API}/admin/articles/${editing}`, payload);
        setNotice("Article updated.");
      }
      await loadArticles();
      setEditing(null);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Save failed. Please try again.");
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete "${slug}" permanently?`)) return;
    try {
      await axios.delete(`${API}/admin/articles/${slug}`);
      await loadArticles();
      setNotice("Article deleted.");
    } catch {
      setError("Delete failed.");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setError(""); setNotice("");
    if (pw.next !== pw.confirm) { setError("New passwords do not match."); return; }
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: pw.current, new_password: pw.next });
      setNotice("Password updated. Use the new password next time you sign in.");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Password change failed.");
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`).catch(() => {});
    navigate("/admin/login");
  };

  const previewHtml = useMemo(() => marked.parse(form.body || ""), [form.body]);

  if (checking) {
    return <section className="flex min-h-[60vh] items-center justify-center"><p className="text-sm text-[#5A6B8C]">Checking session…</p></section>;
  }
  if (!user) return null;

  return (
    <section data-testid="admin-dashboard" className="bg-[#F5F6F8] py-12">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-serif2 text-3xl font-semibold text-[#1F2D4E]">Admin</h1>
            <p className="mt-1 text-sm text-[#5A6B8C]">Signed in as {user.email}</p>
          </div>
          <div className="flex gap-3">
            {tab === "articles" && editing === null && (
              <button data-testid="new-article-button" onClick={startNew} className="flex items-center gap-2 bg-[#1F2D4E] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#B08D3F]">
                <Plus size={16} /> New article
              </button>
            )}
            <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 border border-[#D4D8E0] bg-white px-5 py-2.5 text-sm font-semibold text-[#1F2D4E] transition-colors hover:border-[#B08D3F]">
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>

        <div className="mt-8 flex gap-1 border-b border-[#E4E7EC]">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => { setTab(t.id); setEditing(null); setNotice(""); setError(""); if (t.id === "submissions") loadSubmissions(); }}
              className={`px-5 py-3 text-sm font-semibold transition-colors ${
                tab === t.id ? "border-b-2 border-[#B08D3F] text-[#1F2D4E]" : "text-[#98A0B3] hover:text-[#1F2D4E]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {notice && <p data-testid="admin-notice" className="mt-6 border border-[#B08D3F]/40 bg-white px-5 py-3 text-sm text-[#1F2D4E]">{notice}</p>}
        {error && <p data-testid="admin-error" className="mt-6 border border-red-300 bg-white px-5 py-3 text-sm text-red-600">{error}</p>}

        {tab === "articles" && (editing === null ? (
          <div className="mt-8 border border-[#E4E7EC] bg-white">
            {articles.length === 0 && <p className="p-8 text-sm text-[#5A6B8C]">No articles yet.</p>}
            {articles.map((a) => (
              <div key={a.slug} data-testid={`admin-article-${a.slug}`} className="flex flex-wrap items-center justify-between gap-4 border-b border-[#E4E7EC] px-6 py-5 last:border-b-0">
                <div>
                  <p className="font-serif2 text-lg font-semibold text-[#1F2D4E]">{a.title}</p>
                  <p className="mt-1 text-xs text-[#98A0B3]">
                    {a.category}{a.date ? ` · ${a.date}` : ""} · /insights/{a.slug}
                    {a.pdf && <span className="ml-2 text-[#B08D3F]">PDF</span>}
                    {a.coming_soon && <span className="ml-2 border border-[#B08D3F]/50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-[#B08D3F]">Hidden</span>}
                  </p>
                </div>
                <div className="flex gap-2">
                  {!a.coming_soon && <Link to={`/insights/${a.slug}`} data-testid={`view-${a.slug}`} className="border border-[#D4D8E0] px-4 py-2 text-xs font-semibold text-[#5A6B8C] transition-colors hover:border-[#B08D3F]">View</Link>}
                  <button data-testid={`edit-${a.slug}`} onClick={() => startEdit(a)} className="flex items-center gap-1.5 border border-[#D4D8E0] px-4 py-2 text-xs font-semibold text-[#1F2D4E] transition-colors hover:border-[#B08D3F]"><Pencil size={13} /> Edit</button>
                  <button data-testid={`delete-${a.slug}`} onClick={() => remove(a.slug)} className="flex items-center gap-1.5 border border-[#D4D8E0] px-4 py-2 text-xs font-semibold text-red-600 transition-colors hover:border-red-400"><Trash2 size={13} /> Delete</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <form onSubmit={save} data-testid="article-editor" className="mt-8 border border-[#E4E7EC] bg-white p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-serif2 text-xl font-semibold text-[#1F2D4E]">{editing === "new" ? "New article" : `Editing: ${editing}`}</h2>
              <button type="button" onClick={() => setEditing(null)} className="text-sm font-semibold text-[#5A6B8C] hover:text-[#B08D3F]">Cancel</button>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={labelCls}>Title *</label>
                <input data-testid="article-title" required value={form.title} onChange={set("title")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Slug (leave blank to auto-generate)</label>
                <input data-testid="article-slug" value={form.slug} onChange={set("slug")} placeholder="my-article-slug" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Date</label>
                <input data-testid="article-date" type="date" value={form.date} onChange={set("date")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Author</label>
                <input data-testid="article-author" value={form.author} onChange={set("author")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Category</label>
                <input data-testid="article-category" value={form.category} onChange={set("category")} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Summary (shown on the Insights hub)</label>
                <input data-testid="article-summary" value={form.summary} onChange={set("summary")} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>PDF attachment (optional)</label>
                <div className="flex items-center gap-4">
                  <label data-testid="pdf-upload-label" className="flex cursor-pointer items-center gap-2 border border-[#D4D8E0] bg-white px-5 py-3 text-sm font-semibold text-[#1F2D4E] transition-colors hover:border-[#B08D3F]">
                    <Upload size={15} /> {uploading ? "Uploading…" : "Upload PDF"}
                    <input data-testid="pdf-upload-input" type="file" accept="application/pdf" className="hidden" onChange={uploadPdf} disabled={uploading} />
                  </label>
                  {form.pdf && (
                    <span className="flex items-center gap-2 text-sm text-[#5A6B8C]">
                      <FileText size={15} className="text-[#B08D3F]" /> Attached
                      <button type="button" data-testid="pdf-remove" onClick={() => setForm({ ...form, pdf: "" })} className="text-xs text-red-600 underline underline-offset-2">remove</button>
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-5">
              <div className="flex items-center justify-between">
                <label className={labelCls}>Body (markdown)</label>
                <button type="button" data-testid="toggle-preview" onClick={() => setShowPreview(!showPreview)} className="flex items-center gap-1.5 text-xs font-semibold text-[#B08D3F]">
                  {showPreview ? <><EyeOff size={14} /> Hide preview</> : <><Eye size={14} /> Preview</>}
                </button>
              </div>
              <textarea
                data-testid="article-body"
                rows={14}
                value={form.body}
                onChange={set("body")}
                placeholder={"## First section\n\nWrite the article here. Markdown is supported."}
                className={`${inputCls} resize-y font-mono text-[13px] leading-relaxed`}
              />
              {showPreview && (
                <div data-testid="article-preview" className="prose-ri mt-4 border border-[#E4E7EC] bg-[#F5F6F8] p-6" dangerouslySetInnerHTML={{ __html: previewHtml }} />
              )}
            </div>
            <label className="mt-5 flex items-center gap-3 text-sm text-[#1F2D4E]">
              <input data-testid="article-coming-soon" type="checkbox" checked={form.coming_soon} onChange={set("coming_soon")} className="h-4 w-4 accent-[#B08D3F]" />
              Keep hidden (shows as "Coming soon" on the hub, no public article page)
            </label>
            <div className="mt-8 flex gap-3">
              <button type="submit" data-testid="article-save" className="bg-[#1F2D4E] px-8 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-[#B08D3F]">
                {editing === "new" ? "Publish article" : "Save changes"}
              </button>
            </div>
          </form>
        ))}

        {tab === "submissions" && (
          <div className="mt-8 space-y-10">
            <div>
              <h2 className="font-serif2 text-xl font-semibold text-[#1F2D4E]">Briefing requests <span className="text-sm font-normal text-[#98A0B3]">({leads.length})</span></h2>
              <div data-testid="leads-table" className="mt-4 overflow-x-auto border border-[#E4E7EC] bg-white">
                {leads.length === 0 ? <p className="p-8 text-sm text-[#5A6B8C]">No briefing requests yet.</p> : (
                  <table className="w-full min-w-[720px] text-left text-sm">
                    <thead>
                      <tr className="border-b border-[#E4E7EC]">
                        {["Date", "Name", "Organization", "Role", "Email", "Message"].map((h) => (
                          <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-[#5A6B8C]">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map((l) => (
                        <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-[#E4E7EC] last:border-b-0">
                          <td className="px-4 py-3 text-xs text-[#98A0B3]">{(l.created_at || "").slice(0, 10)}</td>
                          <td className="px-4 py-3 font-medium text-[#1F2D4E]">{l.name}</td>
                          <td className="px-4 py-3 text-[#5A6B8C]">{l.organization}</td>
                          <td className="px-4 py-3 text-[#5A6B8C]">{l.role || ""}</td>
                          <td className="px-4 py-3"><a href={`mailto:${l.email}`} className="text-[#B08D3F] underline underline-offset-2">{l.email}</a></td>
                          <td className="max-w-[240px] truncate px-4 py-3 text-[#5A6B8C]" title={l.message || ""}>{l.message || ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            <div>
              <h2 className="font-serif2 text-xl font-semibold text-[#1F2D4E]">Newsletter subscribers <span className="text-sm font-normal text-[#98A0B3]">({subs.length})</span></h2>
              <div data-testid="subscribers-table" className="mt-4 border border-[#E4E7EC] bg-white">
                {subs.length === 0 ? <p className="p-8 text-sm text-[#5A6B8C]">No subscribers yet.</p> : (
                  subs.map((s) => (
                    <div key={s.id} data-testid={`subscriber-row-${s.id}`} className="flex items-center justify-between border-b border-[#E4E7EC] px-5 py-3 text-sm last:border-b-0">
                      <span className="text-[#1F2D4E]">{s.email}</span>
                      <span className="text-xs text-[#98A0B3]">{(s.created_at || "").slice(0, 10)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {tab === "settings" && (
          <form onSubmit={changePassword} data-testid="password-form" className="mt-8 max-w-md border border-[#E4E7EC] bg-white p-8">
            <h2 className="font-serif2 text-xl font-semibold text-[#1F2D4E]">Change password</h2>
            <div className="mt-6 space-y-5">
              <div>
                <label className={labelCls}>Current password</label>
                <input data-testid="pw-current" type="password" required value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>New password (min 10 characters)</label>
                <input data-testid="pw-next" type="password" required minLength={10} value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Confirm new password</label>
                <input data-testid="pw-confirm" type="password" required value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className={inputCls} />
              </div>
              <button type="submit" data-testid="pw-save" className="bg-[#1F2D4E] px-8 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-[#B08D3F]">
                Update password
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
